// CE-04g-UI-vwap-window — chart-layer helpers for the 5 Session VWAP
// variants. The math layer (`calcSessionVWAP` in `src/lib/indicators.js`)
// keeps its byte-for-byte behavior for the strategy/snapshot path. The
// chart owns the "render only the current window" display policy: filter
// the input bars to the appropriate window, then run a simple cumulative
// VWAP anchored at the first bar of the filtered subset.
//
// This module imports the ET keying primitives from `sessionBoundary.js`
// and MUST NOT import from `src/lib/indicators.js` — keep the math layer
// truly untouched.

import {
  getEtSessionDateKey,
  getEtWeekAnchorKeyFromDateKey,
  getPreviousEtTradingDayKey,
} from './sessionBoundary.js'

/**
 * Cumulative VWAP anchored at the first bar of the input array. No
 * internal resets. Designed to be called with a pre-windowed bar subset
 * (e.g., today's RTH bars, or [yesterday + today] rolling 2-day).
 *
 * Math mirrors `calcSessionVWAP` in `src/lib/indicators.js` (OHLC4 typical
 * price, `Number(c.volume) || 0` coercion, `cumVol > 0` denominator guard,
 * non-finite filter at output time) — minus the session-key reset logic.
 *
 * @param {Array<{time:number, open:number, high:number, low:number, close:number, volume:number}>} bars
 * @returns {Array<{time:number, value:number}>}
 */
export function computeWindowVWAP(bars) {
  if (!Array.isArray(bars) || bars.length === 0) return []
  const out = []
  let cumTPV = 0
  let cumVol = 0
  for (let i = 0; i < bars.length; i++) {
    const c = bars[i]
    const tp = (c.open + c.high + c.low + c.close) / 4
    const vol = Number(c.volume) || 0
    cumTPV += tp * vol
    cumVol += vol
    const value = cumVol > 0 ? cumTPV / cumVol : null
    if (value != null && Number.isFinite(value) && Number.isFinite(c.time)) {
      out.push({ time: c.time, value })
    }
  }
  return out
}

function dateKeyFromIbkrYyyymmdd(time) {
  if (typeof time === 'number'
    && Number.isFinite(time)
    && Number.isInteger(time)
    && time >= 19000101
    && time <= 99991231) {
    const y = Math.floor(time / 10000)
    const mo = String(Math.floor((time % 10000) / 100)).padStart(2, '0')
    const d = String(time % 100).padStart(2, '0')
    return `${y}-${mo}-${d}`
  }
  if (typeof time === 'string' && /^\d{8}$/.test(time)) {
    return `${time.slice(0, 4)}-${time.slice(4, 6)}-${time.slice(6, 8)}`
  }
  return null
}

function barDateKey(bar) {
  if (!bar) return null
  const direct = dateKeyFromIbkrYyyymmdd(bar.ibkrDayKey ?? bar.time)
  if (direct) return direct
  return hasRawEpochMs(bar) ? getEtSessionDateKey(bar.rawEpochMs) : null
}

function latestBarDateKey(bars) {
  if (!Array.isArray(bars) || bars.length === 0) return null
  return barDateKey(bars[bars.length - 1])
}

function hasRawEpochMs(bar) {
  return typeof bar?.rawEpochMs === 'number' && Number.isFinite(bar.rawEpochMs)
}

/**
 * Bars on the latest ET date in the input.
 *
 * @param {Array<{rawEpochMs:number}>} bars
 * @returns {Array}
 */
export function filterBarsTo1DayWindow(bars) {
  const latestKey = latestBarDateKey(bars)
  if (!latestKey) return []
  return bars.filter((b) => barDateKey(b) === latestKey)
}

/**
 * Bars on the latest ET date OR the previous trading day (weekend/holiday-
 * aware via `tradingCalendar`). When the cap returns null, falls back to a
 * 1-day window.
 *
 * @param {Array<{rawEpochMs:number}>} bars
 * @param {{ holidays?: Array<string|{date:string}> } | null | undefined} tradingCalendar
 * @returns {Array}
 */
export function filterBarsTo2DayWindow(bars, tradingCalendar) {
  const latestKey = latestBarDateKey(bars)
  if (!latestKey) return []
  const prevKey = getPreviousEtTradingDayKey(latestKey, tradingCalendar)
  if (prevKey == null) {
    return bars.filter((b) => barDateKey(b) === latestKey)
  }
  return bars.filter((b) => {
    const k = barDateKey(b)
    return k === latestKey || k === prevKey
  })
}

/**
 * Bars from the week's anchor (post-holiday-Monday-aware) through latest.
 *
 * @param {Array<{rawEpochMs:number}>} bars
 * @param {{ holidays?: Array<string|{date:string}> } | null | undefined} tradingCalendar
 * @returns {Array}
 */
export function filterBarsToWtdWindow(bars, tradingCalendar) {
  const latestKey = latestBarDateKey(bars)
  if (!latestKey) return []
  const anchorKey = getEtWeekAnchorKeyFromDateKey(latestKey, tradingCalendar)
  if (!anchorKey) return []
  return bars.filter((b) => {
    const k = barDateKey(b)
    return k != null && k >= anchorKey
  })
}

/**
 * Bars in the same ET calendar month as the latest bar.
 *
 * @param {Array<{rawEpochMs:number}>} bars
 * @returns {Array}
 */
export function filterBarsToMtdWindow(bars) {
  const latestKey = latestBarDateKey(bars)
  const latestMonth = latestKey?.slice(0, 7)
  if (!latestMonth) return []
  return bars.filter((b) => barDateKey(b)?.slice(0, 7) === latestMonth)
}

/**
 * Bars in the same ET calendar year as the latest bar.
 *
 * @param {Array<{rawEpochMs:number}>} bars
 * @returns {Array}
 */
export function filterBarsToYtdWindow(bars) {
  const latestKey = latestBarDateKey(bars)
  const latestYear = latestKey?.slice(0, 4)
  if (!latestYear) return []
  return bars.filter((b) => barDateKey(b)?.slice(0, 4) === latestYear)
}
